/**
 * SubstrateRail — narrow persistent icon strip on the right edge.
 *
 * Substrate (per docs/MENU_IA_DIRECTION.md) = the world the app runs in:
 * People, Calendar, Timetable, Org chart, Domains, Workflow templates.
 * Always visible, icons only, label on hover. Sits below the navbar; does
 * not touch it.
 */

import {
  UserGroupIcon,
  CalendarIcon,
  ClockIcon,
  BuildingOffice2Icon,
  Squares2X2Icon,
  ArrowPathRoundedSquareIcon,
  KeyIcon,
  CurrencyDollarIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';
import useSubstratePanelStore from '@/stores/substratePanelStore';
import { useRoleSwitcher } from '@/features/auth/hooks/useRoleSwitcher';
import { useLocation } from 'react-router-dom';
import { ROUTES } from '@/utils/constants';

const SUBSTRATE_ITEMS = [
  { label: 'People', panelKey: 'people', Icon: UserGroupIcon, fullRoute: ROUTES.PEOPLE, companionOnFullPage: true },
  { label: 'Access codes', panelKey: 'accessCodes', Icon: KeyIcon, fullRoute: ROUTES.ACCESS_CODES },
  { label: 'Calendar', panelKey: 'calendar', Icon: CalendarIcon, fullRoute: ROUTES.CALENDAR, companionOnFullPage: true },
  { label: 'Timetable', panelKey: 'timetable', Icon: ClockIcon, fullRoute: ROUTES.TIMETABLE },
  { label: 'Org chart', panelKey: 'orgChart', Icon: BuildingOffice2Icon, fullRoute: ROUTES.ORG_CHART },
  {
    label: 'Domains',
    panelKey: 'domains',
    Icon: Squares2X2Icon,
    adminOnly: true,
    fullRoute: ROUTES.DOMAINS,
  },
  { label: 'Currencies', panelKey: 'currencies', Icon: CurrencyDollarIcon },
  { label: 'Policies', panelKey: 'policies', Icon: ScaleIcon, adminOnly: true, fullRoute: ROUTES.POLICIES_ADMIN },
  { label: 'Workflow templates', panelKey: 'workflows', Icon: ArrowPathRoundedSquareIcon, fullRoute: ROUTES.WORKFLOW_TEMPLATES },
];

const baseClasses =
  'w-9 h-9 flex items-center justify-center rounded-lg transition-colors';
const activeClasses = 'bg-primary text-primary-content';
const inactiveClasses =
  'text-base-content/60 hover:bg-base-200 hover:text-base-content';

const SubstrateRail = () => {
  const location = useLocation();
  const { currentRole } = useRoleSwitcher();
  const activePanel = useSubstratePanelStore((s) => s.activePanel);
  const togglePanel = useSubstratePanelStore((s) => s.togglePanel);
  const isAdminOrDev = currentRole === 'admin' || currentRole === 'dev';

  return (
    <aside
      className="hidden lg:flex sticky top-20 self-start z-40 flex-col items-center gap-1 py-3 bg-base-100 border-l border-base-300 shrink-0"
      style={{ width: 'var(--substrate-rail-w)', height: 'calc(100vh - 5rem)' }}
      aria-label="Substrate"
    >
      {SUBSTRATE_ITEMS.filter((item) => !item.adminOnly || isAdminOrDev).map(({ label, panelKey, Icon, fullRoute, companionOnFullPage = false }) => {
        const isActive = activePanel === panelKey;
        const isFullPage = fullRoute === location.pathname;
        const disabled = isFullPage && !companionOnFullPage;
        return (
          <button
            key={panelKey}
            type="button"
            title={disabled ? `${label} is already open` : label}
            aria-label={label}
            aria-pressed={isActive}
            disabled={disabled}
            onClick={() => togglePanel(panelKey, panelKey === 'people' && isFullPage
              ? { kind: 'people-workspace', view: 'overview', title: 'People tools' }
              : null)}
            className={`${baseClasses} ${
              disabled
                ? 'cursor-default bg-base-200 text-base-content/30'
                : isActive ? activeClasses : inactiveClasses
            }`}
          >
            <Icon className="w-5 h-5" />
          </button>
        );
      })}
    </aside>
  );
};

export default SubstrateRail;
